import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { getDate } from "../components/common/currentDateFormated";
import fetcher from "../utils/fetcher";
import useMlsList from "../components/SearchListings/useMlsList";
import { convertServerToClientListing, convertServerToClientSummaryListing } from "./useListing";
import {
  bathsList,
  bedsList,
  squareFeet as squareFeetList,
  searchDateTypeFilters,
  preforeclosureStatusTypesList,
  statusList,
  distressedLevel,
  communicationTypeFilters,
} from "../data/selectionData";
import { useSearchListingsQuery } from "./useListing";
import { useInfiniteSearchListingsQuery } from "./useInfiniteSearchListings";
import { useSearchCommon } from "./useSearchCommon";
import { EnumProductTypes, Listing, ContactInfo, SummaryListing } from "../types/listing";
import { DateSearchType } from "../components/SearchListings/SearchFilters/SmartDateFilter";
import useAccount from "./useAccount";
import { PROPERTY_TYPE_OPTIONS } from "../components/neighborhoodSearch/constants";

interface FilterConfig {
  name: string;
  apiKeyName?: string;
  excludeFromSearchQuery?: boolean;
  prepareApiValue?: (value: unknown) => unknown;
}

interface Filter {
  name: string;
  value: unknown;
  label?: string;
  apiKeyName?: string;
  defaultValue?: unknown;
  excludeFromSearchQuery?: boolean;
  excludeFromReset?: boolean;
  isFilter?: boolean;
  isEnabled?: boolean;
  prepareApiValue?: (value: unknown) => unknown;
}

interface ActiveFilter {
  label: string;
  delete?: () => void;
  noTruncate?: boolean;
}

interface SearchListingsData {
  listings: Listing[];
  total_records: number;
  lists?: Array<{ id: number; name: string }>;
}

interface SummarySearchListingsData {
  listings: SummaryListing[];
  total_records: number;
}

interface UseSearchListingsParams {
  defaultBatchId?: string;
  defaultLookupId?: string;
  productType?: EnumProductTypes;
  searchQueryKey?: string;
  fromDateDaysOffset?: number;
  defaultSortField?: string;
  defaultSortDir?: string;
  defaultPageSize?: number;
  defaultToDate?: string;
  defaultCommType?: string;
  defaultSearchDateType?: string;
  defaultFromDate?: string;
  searchUrl?: string;
  suppressPriceFilters?: boolean;
  includePreForeclosureStatusFilter?: boolean;
  includeDistressedFilters?: boolean;
  includeListingStatusFilter?: boolean;
  includeSearchDateTypeFilter?: boolean;
  includeMLSActivesFilter?: boolean;
  includeBatchIdFilter?: boolean;
  includeLookupNameFilter?: boolean;
  includeLookupSourceFilter?: boolean;
  includeMlsFilter?: boolean;
  includePropertyTypeFilter?: boolean;
  propertyTypeOptions?: readonly { value: string; label: string }[];
  defaultPropertyTypes?: string[];
}

interface UseSearchListingsResult {
  filters: Filter[];
  activeFilters: ActiveFilter[];
  sortDir: string;
  sortField: string;
  commType: string;
  toDate: string;
  fromDate: string;
  minPrice: string;
  maxPrice: string;
  numBeds: string;
  numBaths: string;
  sqFootage: string;
  postalCode: string;
  tags: string;
  isTagsAndSearch: boolean;
  setIsTagsAndSearch: (isAndSearch: boolean) => void;
  // Infinite scroll properties
  infiniteListings: Listing[];
  infiniteListingsIsLoading: boolean;
  infiniteListingsFetchNextPage: () => void;
  infiniteListingsHasNextPage: boolean;
  mls: string;
  batchId: string;
  listingStatuses: string[];
  searchDateType: string;
  searchParams: URLSearchParams;
  currentPage: number;
  pageSize: number;
  updateSearchParams: (updates: Record<string, unknown>) => void;
  resetSearchFilters: () => void;
  scrollToTop: () => void;
  searchQuery: UseQueryResult<SearchListingsData>;
  allSearchListingsQuery: UseQueryResult<SummarySearchListingsData>;
  allListingsQuery: UseQueryResult<SearchListingsData>;
  fetchListingsInChunks: (
    onProgress: (completed: number, total: number) => void,
    cancelled: { current: boolean },
  ) => Promise<Listing[]>;
  csvApiUrl: string;
  isHideDNCPhoneNumbers: boolean;
  suppressPriceFilters: boolean;
  includePreForeclosureStatusFilter: boolean;
  includeDistressedFilters: boolean;
  includeListingStatusFilter: boolean;
  includeSearchDateTypeFilter: boolean;
  includeMLSActivesFilter: boolean;
  includeBatchIdFilter: boolean;
  includeLookupNameFilter: boolean;
  includeLookupSourceFilter: boolean;
  includeMlsFilter: boolean;
  includePropertyTypeFilter: boolean;
  importedListingsImports: Array<{ id: number; name: string }>;
  totalRecords: number;
  listings: Listing[];
  productType: EnumProductTypes;
  /** Key that changes when search criteria change (excluding pagination) */
  searchCriteriaKey: string;
}

const DEFAULT_PREFORECLOSURE_STATUS_TYPES = preforeclosureStatusTypesList.map(
  ({ code }: { code: string }) => code
);

const useSearchListings = ({
  defaultBatchId,
  defaultLookupId,
  productType,
  searchQueryKey,
  fromDateDaysOffset = 30,
  defaultSortField = "date",
  defaultSortDir = "desc",
  defaultPageSize = 25,
  defaultToDate = getDate(0),
  defaultCommType = "",
  defaultSearchDateType = "file_upload_date",
  defaultFromDate = getDate(-fromDateDaysOffset),
  searchUrl = "/api/expired/listing",
  suppressPriceFilters = false,
  includePreForeclosureStatusFilter = false,
  includeDistressedFilters = false,
  includeListingStatusFilter = false,
  includeSearchDateTypeFilter = false,
  includeMLSActivesFilter = false,
  includeBatchIdFilter = false,
  includeLookupNameFilter = false,
  includeLookupSourceFilter = false,
  includeMlsFilter = false,
  includePropertyTypeFilter = false,
  propertyTypeOptions = PROPERTY_TYPE_OPTIONS,
  defaultPropertyTypes = [],
}: UseSearchListingsParams = {}): UseSearchListingsResult => {
  const mlsList = useMlsList();
  const isFirstLoad = useRef(true);
  const { data: accountData } = useAccount();

  // Define filter configurations for API mapping
  const filterConfigs: FilterConfig[] = [
    {
      name: "currentPage",
      excludeFromSearchQuery: true,
    },
    {
      name: "pageSize",
      apiKeyName: "limit",
    },
    {
      name: "sortDir",
      apiKeyName: "sort_dir",
    },
    {
      name: "sortField",
      apiKeyName: "sort",
    },
    {
      name: "commType",
      apiKeyName: "lead_filter",
    },
    {
      name: "toDate",
      apiKeyName: "to_date",
    },
    {
      name: "fromDate",
      apiKeyName: "from_date",
    },
    {
      name: "dateSearchType",
      excludeFromSearchQuery: true,
    },
    {
      name: "minPrice",
      apiKeyName: "min_price",
    },
    {
      name: "maxPrice",
      apiKeyName: "max_price",
    },
    {
      name: "numBeds",
      apiKeyName: "beds",
    },
    {
      name: "numBaths",
      apiKeyName: "baths",
    },
    {
      name: "sqFootage",
      apiKeyName: "sq_ft",
    },
    {
      name: "postalCode",
      apiKeyName: "postal_codes",
    },
    {
      name: "mls",
      apiKeyName: "mls",
    },
    {
      name: "tags",
      apiKeyName: "tags",
    },
    {
      name: "isTagsAndSearch",
      excludeFromSearchQuery: true,
    },
    {
      name: "searchDateType",
      apiKeyName: "date_search_type",
    },
    {
      name: "status",
      apiKeyName: "status",
    },
    {
      name: "showMLSActives",
      apiKeyName: "show_mls_active",
    },
    {
      name: "isHideDNCPhoneNumbers",
      excludeFromSearchQuery: true,
    },
    {
      name: "distressedLevel",
      apiKeyName: "distressed_level",
    },
    {
      name: "preForeclosureStatus",
      apiKeyName: "listing_type",
    },
    {
      name: "batchId",
      apiKeyName: "batch_id",
    },
    {
      name: "lookupIds",
      apiKeyName: "list_id",
      prepareApiValue: (value) => ((value as string[]) || []).join(","),
    },
    {
      name: "lookupSources",
      apiKeyName: "source",
      prepareApiValue: (value) => (value as string[]).join("|"),
    },
    {
      name: "propertyTypes",
      apiKeyName: "property_type",
      prepareApiValue: (value) => (value as string[]).join(","),
    },
  ];

  // Use the common search hook for basic parameters
  const {
    currentPage,
    pageSize,
    sortField,
    sortDir,
    customFilters,
    searchApiQueryParams,
    updateSearchParams,
    resetFilters,
    scrollToTop,
    searchParams,
  } = useSearchCommon({
    defaultPageSize,
    defaultSortField,
    defaultSortDir,
    customFilters: {
      commType: defaultCommType,
      toDate: defaultToDate,
      fromDate: defaultFromDate,
      dateSearchType: DateSearchType.CUSTOM,
      minPrice: "",
      maxPrice: "",
      numBeds: "",
      numBaths: "",
      sqFootage: "",
      postalCode: "",
      mls: "",
      tags: "",
      isTagsAndSearch: false,
      searchDateType: defaultSearchDateType,
      status: defaultBatchId ? [] : ["X", "W", "C"],
      showMLSActives: false,
      isHideDNCPhoneNumbers: accountData?.is_hide_dnc_phone_numbers || false,
      distressedLevel: [],
      preForeclosureStatus: [],
      batchId: defaultBatchId || "",
      lookupIds: defaultLookupId ? [defaultLookupId] : [],
      lookupSources: [],
      propertyTypes: defaultPropertyTypes,
    },
    filterConfigs,
  });

  // Extract filter values from customFilters
  const commType = customFilters.commType as string;
  const toDate = customFilters.toDate as string;
  const fromDate = customFilters.fromDate as string;
  const dateSearchType = customFilters.dateSearchType as string;
  const minPrice = customFilters.minPrice as string;
  const maxPrice = customFilters.maxPrice as string;
  const numBeds = customFilters.numBeds as string;
  const numBaths = customFilters.numBaths as string;
  const batchId = customFilters.batchId as string;
  const lookupSources = customFilters.lookupSources as string[];
  const showMLSActives = customFilters.showMLSActives as boolean;
  const isHideDNCPhoneNumbers = customFilters.isHideDNCPhoneNumbers as boolean;
  const sqFootage = customFilters.sqFootage as string;
  const postalCode = customFilters.postalCode as string;
  const mls = customFilters.mls as string;
  const tags = customFilters.tags as string;
  const isTagsAndSearch = customFilters.isTagsAndSearch as boolean;
  const searchDateType = customFilters.searchDateType as string;

  const distressedLevels = customFilters.distressedLevel as string[];
  const propertyTypes = customFilters.propertyTypes as string[];

  const lookupIds = useMemo(() => {
    if (!includeLookupNameFilter) {
      return [];
    }

    const lkUpIds = customFilters.lookupIds as string[];
    if (lkUpIds && lkUpIds.length) {
      return lkUpIds;
    }

    if (isFirstLoad.current && defaultLookupId) {
      return [defaultLookupId];
    }

    return [];
  }, [customFilters.lookupIds, includeLookupNameFilter, defaultLookupId]);

  const preForeclosureStatuses = useMemo(() => {
    if (!includePreForeclosureStatusFilter) {
      return [];
    }

    const preForeclosureStatuses =
      customFilters.preForeclosureStatus as string[];
    if (preForeclosureStatuses && preForeclosureStatuses.length) {
      return preForeclosureStatuses;
    }

    if (isFirstLoad.current) {
      return DEFAULT_PREFORECLOSURE_STATUS_TYPES;
    }

    return [];
  }, [customFilters.preForeclosureStatus, includePreForeclosureStatusFilter]);

  const listingStatuses = useMemo(() => {
    if (!includeListingStatusFilter) {
      return [];
    }
    const statuses = searchParams.getAll("status");
    if (statuses.length) {
      return statuses;
    }

    if (isFirstLoad.current && !batchId) {
      return ["X", "W", "C"];
    }
    return [];
  }, [searchParams, includeListingStatusFilter, batchId]);

  const filters: Filter[] = useMemo(() => {
    return [
      {
        name: "currentPage",
        value: currentPage,
        defaultValue: 1,
        excludeFromSearchQuery: true,
      },
      {
        name: "pageSize",
        value: pageSize,
        label: "Records Per Page",
        apiKeyName: "limit",
        defaultValue: defaultPageSize,
        excludeFromReset: true,
      },
      {
        name: "sortDir",
        value: sortDir,
        label: "Sort Direction",
        apiKeyName: "sort_dir",
        defaultValue: defaultSortDir,
        excludeFromReset: true,
      },
      {
        name: "sortField",
        value: sortField,
        label: "Sort Field",
        apiKeyName: "sort",
        defaultValue: defaultSortField,
        excludeFromReset: true,
      },
      {
        name: "commType",
        value: commType,
        label: "Comm Type",
        apiKeyName: "lead_filter",
        isFilter: true,
        isEnabled: true,
        defaultValue: defaultCommType,
      },
      {
        name: "toDate",
        value: toDate,
        label: "To Date",
        apiKeyName: "to_date",
        isFilter: true,
        isEnabled: true,
        defaultValue: defaultToDate,
      },
      {
        name: "fromDate",
        value: fromDate,
        label: "From Date",
        apiKeyName: "from_date",
        isFilter: true,
        isEnabled: true,
        defaultValue: defaultFromDate,
      },
      {
        name: "dateSearchType",
        value: dateSearchType,
        defaultValue: DateSearchType.CUSTOM,
        excludeFromSearchQuery: true,
      },
      {
        name: "minPrice",
        value: minPrice,
        label: "Min Price",
        apiKeyName: "min_price",
        isEnabled: !suppressPriceFilters,
        isFilter: true,
      },
      {
        name: "maxPrice",
        value: maxPrice,
        label: "Max Price",
        apiKeyName: "max_price",
        isEnabled: !suppressPriceFilters,
        isFilter: true,
      },
      {
        name: "numBeds",
        value: numBeds,
        label: "Beds",
        apiKeyName: "beds",
        isEnabled: true,
        isFilter: true,
      },
      {
        name: "numBaths",
        value: numBaths,
        label: "Baths",
        apiKeyName: "baths",
        isEnabled: true,
        isFilter: true,
      },
      {
        name: "sqFootage",
        value: sqFootage,
        label: "Sq Ft",
        apiKeyName: "sq_ft",
        isEnabled: true,
        isFilter: true,
      },
      {
        name: "postalCode",
        value: postalCode,
        label: "Postal Codes",
        apiKeyName: "postal_codes",
        isEnabled: true,
        isFilter: true,
      },
      {
        name: "mls",
        value: mls,
        label: "MLS",
        apiKeyName: "mls",
        isEnabled: includeMlsFilter,
        isFilter: true,
      },
      {
        name: "tags",
        value: tags,
        label: "Tags",
        apiKeyName: "tags",
        isEnabled: true,
        isFilter: true,
      },
      {
        name: "isTagsAndSearch",
        value: isTagsAndSearch,
        label: "Tags AND Search",
        excludeFromSearchQuery: true,
        isEnabled: true,
        isFilter: true,
        defaultValue: false,
      },
      {
        name: "searchDateType",
        value: searchDateType,
        label: "Search Date",
        apiKeyName: "date_search_type",
        isEnabled: includeSearchDateTypeFilter,
        isFilter: true,
        defaultValue: defaultSearchDateType,
      },
      {
        name: "status",
        value: listingStatuses,
        label: "Status",
        apiKeyName: "status",
        isEnabled: includeListingStatusFilter,
        isFilter: true,
        defaultValue: batchId ? [] : ["X", "W", "C"],
      },
      {
        name: "showMLSActives",
        value: showMLSActives,
        label: "Show MLS Actives",
        apiKeyName: "show_mls_active",
        isEnabled: includeMLSActivesFilter,
        isFilter: true,
        defaultValue: false,
      },
      {
        name: "isHideDNCPhoneNumbers",
        value: isHideDNCPhoneNumbers,
        label: "Hide DNC Phone Numbers",
        excludeFromSearchQuery: true,
        isEnabled: true,
        isFilter: true,
        defaultValue: accountData?.is_hide_dnc_phone_numbers || false,
      },
      {
        name: "distressedLevel",
        value: distressedLevels,
        label: "Distressed Level",
        apiKeyName: "distressed_level",
        isEnabled: includeDistressedFilters,
        isFilter: true,
        defaultValue: [],
      },
      {
        name: "preForeclosureStatus",
        value: preForeclosureStatuses,
        label: "Pre-Foreclosure Status",
        apiKeyName: "listing_type",
        isEnabled: includePreForeclosureStatusFilter,
        isFilter: true,
        defaultValue: DEFAULT_PREFORECLOSURE_STATUS_TYPES,
      },
      {
        name: "batchId",
        value: batchId,
        label: "Batch ID",
        apiKeyName: "batch_id",
        isEnabled: includeBatchIdFilter,
        isFilter: true,
      },
      {
        name: "lookupIds",
        value: lookupIds,
        label: "Name",
        apiKeyName: "list_id",
        isEnabled: includeLookupNameFilter,
        isFilter: true,
        prepareApiValue: (value) => ((value as string[]) || []).join(","),
        defaultValue: [],
      },
      {
        name: "lookupSources",
        value: lookupSources,
        label: "Source",
        apiKeyName: "source",
        isEnabled: includeLookupSourceFilter,
        isFilter: true,
        prepareApiValue: (value) => (value as string[]).join("|"),
        defaultValue: [],
      },
      {
        name: "propertyTypes",
        value: propertyTypes,
        label: "Property Type",
        apiKeyName: "property_type",
        isEnabled: includePropertyTypeFilter,
        isFilter: true,
        prepareApiValue: (value) => (value as string[]).join(","),
        defaultValue: defaultPropertyTypes,
      },
    ];
  }, [
    suppressPriceFilters,
    isTagsAndSearch,
    defaultCommType,
    defaultPageSize,
    defaultSortDir,
    defaultSortField,
    defaultToDate,
    currentPage,
    includeMlsFilter,
    defaultFromDate,
    includeBatchIdFilter,
    includeMLSActivesFilter,
    batchId,
    preForeclosureStatuses,
    distressedLevels,
    showMLSActives,
    isHideDNCPhoneNumbers,
    pageSize,
    sortDir,
    sortField,
    commType,
    toDate,
    fromDate,
    dateSearchType,
    minPrice,
    maxPrice,
    numBeds,
    numBaths,
    sqFootage,
    postalCode,
    mls,
    tags,
    searchDateType,
    listingStatuses,
    lookupIds,
    includeSearchDateTypeFilter,
    includeListingStatusFilter,
    includeDistressedFilters,
    includePreForeclosureStatusFilter,
    defaultSearchDateType,
    includeLookupNameFilter,
    includeLookupSourceFilter,
    lookupSources,
    includePropertyTypeFilter,
    propertyTypes,
    defaultPropertyTypes,
    accountData?.is_hide_dnc_phone_numbers,
  ]);

  const enabledFilters = useMemo(() => {
    return filters.filter(
      ({ isEnabled, isFilter }) => (isEnabled || undefined) && isFilter
    );
  }, [filters]);

  // searchApiQueryParams is now provided by useSearchCommon

  // Add debouncing to searchApiQueryParams to prevent rapid API calls
  const [debouncedSearchApiQueryParams, setDebouncedSearchApiQueryParams] =
    useState(searchApiQueryParams);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchApiQueryParams(searchApiQueryParams);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchApiQueryParams]);

  const searchQuery = useSearchListingsQuery({
    searchQueryKey,
    searchApiQueryParams: debouncedSearchApiQueryParams,
    currentPage,
    pageSize,
    sortField,
    sortDir,
    searchUrl,
    productType,
  });

  // Infinite query for sidebar listings
  const infiniteSearchQuery = useInfiniteSearchListingsQuery({
    searchQueryKey: `${searchQueryKey}-infinite`,
    searchApiQueryParams: debouncedSearchApiQueryParams,
    pageSize,
    sortField,
    sortDir,
    searchUrl,
    productType,
  });

  const { total_records: totalRecords, listings } = searchQuery.data || {};

  // Extract infinite listings from all pages (already sorted by queryFn)
  const infiniteListings = useMemo(() => {
    return infiniteSearchQuery?.data?.pages.flatMap(page => page.listings || []) || [];
  }, [infiniteSearchQuery?.data?.pages]);

  const importedListingsImports = useMemo(
    () => searchQuery.data?.lists || [],
    [searchQuery.data]
  );

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const updatedActiveFilters: ActiveFilter[] = [];
    if (fromDate && toDate) {
      // Get preset label or fall back to date range
      const presetLabels: Record<string, string> = {
        [DateSearchType.TODAY]: "Today",
        [DateSearchType.LAST_7_DAYS]: "Last 7 days",
        [DateSearchType.LAST_30_DAYS]: "Last 30 days",
      };
      const dateLabel = presetLabels[dateSearchType] || `${fromDate} - ${toDate}`;

      // Get the search date type name (Delivery Date, Status Date)
      const searchDateTypeFilter = enabledFilters.find(
        ({ name }) => name === "searchDateType"
      );
      const searchDateTypeName = searchDateTypeFilter?.value
        ? searchDateTypeFilters.find(
            ({ value }: { value: string }) => searchDateTypeFilter.value === value
          )?.name
        : null;

      const label = searchDateTypeName
        ? `${searchDateTypeName}: ${dateLabel}`
        : dateLabel;

      updatedActiveFilters.push({ label, noTruncate: true });
    }

    const commTypeFilter = enabledFilters.find(
      ({ name }) => name === "commType"
    );
    if (commTypeFilter && commTypeFilter.value) {
      const commTypeValueLabel = communicationTypeFilters.find(
        ({ value }: { value: string }) => value === commTypeFilter.value
      )?.name;
      updatedActiveFilters.push({
        label: commTypeValueLabel,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            commType: "",
          });
        },
      });
    }

    const showMLSActivesFilter = enabledFilters.find(
      ({ name }) => name === "showMLSActives"
    );
    if (showMLSActivesFilter && showMLSActivesFilter.value) {
      updatedActiveFilters.push({
        label: "MLS Actives Included",
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            showMLSActives: false,
          });
        },
      });
    }

    const hideDNCPhoneNumbersFilter = enabledFilters.find(
      ({ name }) => name === "isHideDNCPhoneNumbers"
    );
    if (hideDNCPhoneNumbersFilter && hideDNCPhoneNumbersFilter.value) {
      updatedActiveFilters.push({
        label: "DNC Phone Numbers Hidden",
        delete: () => {
          updateSearchParams({
            isHideDNCPhoneNumbers: false,
          });
        },
      });
    }

    if (
      enabledFilters.find(({ name }) => name === "distressedLevel") &&
      distressedLevels.length
    ) {
      const selectedDistressedLevels = distressedLevel
        .filter(({ value }: { value: string }) =>
          distressedLevels.includes(value)
        )
        .map(({ name }: { name: string }) => name);
      updatedActiveFilters.push({
        label: selectedDistressedLevels.join(", "),
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            distressedLevel: [],
          });
        },
      });
    }

    if (enabledFilters.find(({ name }) => name === "preForeclosureStatus")) {
      preForeclosureStatuses.forEach((status) => {
        const found = preforeclosureStatusTypesList.find(
          ({ code }: { code: string }) => code === status
        );
        if (found) {
          updatedActiveFilters.push({
            label: found.name,
            delete: () => {
              const statuses = searchParams.getAll("preForeclosureStatus");
              updateSearchParams({
                currentPage: 1,
                preForeclosureStatus: statuses.filter((s) => s !== status),
              });
            },
          });
        }
      });
    }

    if (enabledFilters.find(({ name }) => name === "status")) {
      listingStatuses.forEach((status) => {
        const found = statusList.find(
          ({ value }: { value: string }) => value === status
        );
        if (found) {
          updatedActiveFilters.push({
            label: found.name,
            delete: () => {
              const statuses = searchParams.getAll("status");
              updateSearchParams({
                currentPage: 1,
                status: statuses.filter((s) => s !== status),
              });
            },
          });
        }
      });
    }

    const batchIdFilter = enabledFilters.find(({ name }) => name === "batchId");
    if (batchIdFilter && batchIdFilter.value) {
      updatedActiveFilters.push({
        label: `Batch: ${batchIdFilter.value}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            batchId: "",
          });
        },
      });
    }

    if (enabledFilters.find(({ name }) => name === "lookupIds")) {
      lookupIds.forEach((lookupId) => {
        const found = importedListingsImports.find(
          ({ id }) => `${id}` === lookupId
        );
        if (found) {
          updatedActiveFilters.push({
            label: `Name: ${found.name}`,
            delete: () => {
              updateSearchParams({
                currentPage: 1,
                lookupIds: lookupIds.filter((lId) => lId !== lookupId),
              });
            },
          });
        }
      });
    }

    if (enabledFilters.find(({ name }) => name === "lookupSources")) {
      lookupSources.forEach((source) => {
        updatedActiveFilters.push({
          label: `Source: ${source}`,
          delete: () => {
            updateSearchParams({
              currentPage: 1,
              lookupSources: lookupSources.filter((s) => s !== source),
            });
          },
        });
      });
    }

    if (enabledFilters.find(({ name }) => name === "propertyTypes") && propertyTypes.length) {
      const selectedPropertyTypeLabels = propertyTypes
        .map((type) => propertyTypeOptions.find(({ value }) => value === type)?.label)
        .filter(Boolean)
        .join(", ");
      updatedActiveFilters.push({
        label: `Property Type: ${selectedPropertyTypeLabels}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            propertyTypes: [],
          });
        },
      });
    }

    const tagsFilter = enabledFilters.find(({ name }) => name === "tags");
    if (tagsFilter && tagsFilter.value) {
      updatedActiveFilters.push({
        label: `${tagsFilter.label}: ${tagsFilter.value}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            tags: "",
          });
        },
      });
    }

    const minPriceFilter = enabledFilters.find(
      ({ name }) => name === "minPrice"
    );
    if (minPriceFilter && minPriceFilter.value) {
      updatedActiveFilters.push({
        label: `${minPriceFilter.label}: ${minPriceFilter.value}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            minPrice: "",
          });
        },
      });
    }

    const maxPriceFilter = enabledFilters.find(
      ({ name }) => name === "maxPrice"
    );
    if (maxPriceFilter && maxPriceFilter.value) {
      updatedActiveFilters.push({
        label: `${maxPriceFilter.label}: ${maxPriceFilter.value}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            maxPrice: "",
          });
        },
      });
    }

    const numBedsFilter = enabledFilters.find(({ name }) => name === "numBeds");
    if (numBedsFilter && numBedsFilter.value) {
      const numBedsValueLabel = bedsList.find(
        ({ value }: { value: string }) => value === numBedsFilter.value
      )?.name;
      updatedActiveFilters.push({
        label: `${numBedsFilter.label}: ${numBedsValueLabel}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            numBeds: "",
          });
        },
      });
    }

    const numBathsFilter = enabledFilters.find(
      ({ name }) => name === "numBaths"
    );
    if (numBathsFilter && numBathsFilter.value) {
      const numBathsValueLabel = bathsList.find(
        ({ value }: { value: string }) => value === numBathsFilter.value
      )?.name;
      updatedActiveFilters.push({
        label: `${numBathsFilter.label}: ${numBathsValueLabel}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            numBaths: "",
          });
        },
      });
    }

    const sqFootageFilter = enabledFilters.find(
      ({ name }) => name === "sqFootage"
    );
    if (sqFootageFilter && sqFootageFilter.value) {
      const sqFootageLabel = squareFeetList.find(
        ({ value }: { value: string }) => value === sqFootageFilter.value
      )?.name;
      updatedActiveFilters.push({
        label: `${sqFootageFilter.label}: ${sqFootageLabel}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            sqFootage: "",
          });
        },
      });
    }

    const postalCodeFilter = enabledFilters.find(
      ({ name }) => name === "postalCode"
    );
    if (postalCodeFilter && postalCodeFilter.value) {
      updatedActiveFilters.push({
        label: `${postalCodeFilter.label}: ${postalCodeFilter.value}`,
        delete: () => {
          updateSearchParams({
            currentPage: 1,
            postalCode: "",
          });
        },
      });
    }

    const mlsFilter = enabledFilters.find(({ name }) => name === "mls");
    if (mlsFilter && mlsFilter.value) {
      const selectMlsListItem = mlsList?.find(
        ({ customer_mls_id }: { customer_mls_id: string }) =>
          `${customer_mls_id}` === mlsFilter.value
      );
      if (selectMlsListItem) {
        updatedActiveFilters.push({
          label: `${mlsFilter.label}: ${selectMlsListItem.name}`,
          delete: () => {
            updateSearchParams({
              currentPage: 1,
              mls: "",
            });
          },
        });
      }
    }

    return updatedActiveFilters;
  }, [
    lookupIds,
    lookupSources,
    propertyTypes,
    propertyTypeOptions,
    mlsList,
    enabledFilters,
    updateSearchParams,
    searchParams,
    distressedLevels,
    preForeclosureStatuses,
    listingStatuses,
    fromDate,
    toDate,
    dateSearchType,
    importedListingsImports,
  ]);

  const allSearchListingsQuery = useQuery<SummarySearchListingsData>({
    queryKey: [
      `${searchQueryKey}-all`,
      debouncedSearchApiQueryParams.toString(),
    ],
    enabled: !!(
      currentPage &&
      pageSize &&
      sortField &&
      sortDir &&
      totalRecords
    ),
    staleTime: 30000, // Cache for 30 seconds
    queryFn: async () => {
      // Only fetch if we have more records than fit on one page
      if (totalRecords <= pageSize) {
        // If all records fit on one page, just return the current search results
        return searchQuery.data || { listings: [], total_records: 0 };
      }

      const allListingsApiQueryParams = new URLSearchParams(
        searchApiQueryParams.toString()
      );
      // Fetch all listings up to a reasonable limit (e.g., 1000 for map display)
      const mapListingsLimit = Math.min(totalRecords, 1000);
      allListingsApiQueryParams.set("limit", String(mapListingsLimit));
      allListingsApiQueryParams.set("offset", "0");
      allListingsApiQueryParams.set("summary", "1");

      const resp = await fetcher({
        url: `${searchUrl}?${allListingsApiQueryParams.toString()}`,
      });

      if (!productType) {
        throw new Error('productType is required for convertServerToClientSummaryListing');
      }
      const processedListings = resp.data.listings.map((listing: Record<string, any>) =>
        convertServerToClientSummaryListing(listing, productType)
      );

      return {
        listings: processedListings,
        total_records: resp.data.total_records,
      };
    },
  });

  const allListingsQuery = useQuery<SearchListingsData>({
    queryKey: [`${searchQueryKey}-all-listings`, isHideDNCPhoneNumbers],
    enabled: false,
    staleTime: Infinity,
    queryFn: async () => {
      const searchForPrintApiQueryParams = new URLSearchParams(
        searchApiQueryParams.toString()
      );
      searchForPrintApiQueryParams.set("limit", String(totalRecords));
      searchForPrintApiQueryParams.set("offset", "0");
      const resp = await fetcher({
        url: `${searchUrl}?${searchForPrintApiQueryParams.toString()}`,
      });

      if (!productType) {
        throw new Error('productType is required for convertServerToClientListing');
      }
      let processedListings = resp.data.listings.map((listing: Record<string, any>) =>
        convertServerToClientListing(listing, productType)
      );

      if (isHideDNCPhoneNumbers) {
        // Filter out contacts with DNC flag set to true
        processedListings = processedListings.map((listing: Listing) => ({
          ...listing,
          contacts:
            listing.contacts?.filter((contact: ContactInfo) => !contact.dnc) ||
            [],
        }));
      }

      return {
        listings: processedListings,
        total_records: resp.data.total_records,
      };
    },
  });

  const SEND_CARDS_CHUNK_SIZE = 200;

  const fetchListingsInChunks = useCallback(async (
    onProgress: (completed: number, total: number) => void,
    cancelled: { current: boolean },
  ): Promise<Listing[]> => {
    if (totalRecords <= 0) return [];

    const totalChunks = Math.ceil(totalRecords / SEND_CARDS_CHUNK_SIZE);
    let completedChunks = 0;

    const chunkPromises = Array.from({ length: totalChunks }, (_, i) => {
      const offset = i * SEND_CARDS_CHUNK_SIZE;
      const limit = Math.min(SEND_CARDS_CHUNK_SIZE, totalRecords - offset);

      const params = new URLSearchParams(searchApiQueryParams.toString());
      params.set("limit", String(limit));
      params.set("offset", String(offset));

      return fetcher({ url: `${searchUrl}?${params.toString()}` }).then((resp) => {
        if (cancelled.current) return [];
        completedChunks++;
        onProgress(completedChunks, totalChunks);

        if (!productType) {
          throw new Error('productType is required for convertServerToClientListing');
        }
        let processedListings = resp.data.listings.map((listing: Record<string, any>) =>
          convertServerToClientListing(listing, productType)
        );

        if (isHideDNCPhoneNumbers) {
          processedListings = processedListings.map((listing: Listing) => ({
            ...listing,
            contacts: listing.contacts?.filter((contact: ContactInfo) => !contact.dnc) || [],
          }));
        }

        return processedListings;
      });
    });

    const results = await Promise.all(chunkPromises);
    if (cancelled.current) return [];
    return results.flat();
  }, [searchApiQueryParams, searchUrl, totalRecords, productType, isHideDNCPhoneNumbers]);

  const csvApiUrl = useMemo(() => {
    const searchForCsvApiQueryParams = new URLSearchParams(
      searchApiQueryParams.toString()
    );
    searchForCsvApiQueryParams.set("limit", String(totalRecords));
    searchForCsvApiQueryParams.set(
      "offset",
      String((currentPage - 1) * pageSize)
    );
    return `${searchUrl}/csv?${searchForCsvApiQueryParams.toString()}`;
  }, [searchApiQueryParams, totalRecords, currentPage, pageSize, searchUrl]);

  useEffect(() => {
    // Set the ref to false after the first render
    isFirstLoad.current = false;
  }, []);

  // URL sync is now fully handled by useSearchCommon
  // Removed the useEffect that was causing infinite loops

  // Reset filters to their default values
  const resetSearchFilters = useCallback(() => {
    const resetConfig = filters
      .filter(
        ({ isEnabled, isFilter, excludeFromReset }) =>
          ((isEnabled && isFilter) || !isFilter) && !excludeFromReset
      )
      .map(({ name, defaultValue, excludeFromReset }) => ({
        name,
        defaultValue,
        exclude: excludeFromReset,
      }));

    resetFilters(resetConfig);
  }, [filters, resetFilters]);

  // Generate a key that changes when search criteria change (excluding pagination)
  // This is used to reset the selected listing when performing a new search
  const searchCriteriaKey = useMemo(() => {
    return JSON.stringify({
      sortField,
      sortDir,
      commType,
      toDate,
      fromDate,
      minPrice,
      maxPrice,
      numBeds,
      numBaths,
      sqFootage,
      postalCode,
      tags,
      isTagsAndSearch,
      mls,
      batchId,
      listingStatuses,
      searchDateType,
      isHideDNCPhoneNumbers,
      preForeclosureStatuses,
      distressedLevels,
      propertyTypes,
    });
  }, [
    sortField,
    sortDir,
    commType,
    toDate,
    fromDate,
    minPrice,
    maxPrice,
    numBeds,
    numBaths,
    sqFootage,
    postalCode,
    tags,
    isTagsAndSearch,
    mls,
    batchId,
    listingStatuses,
    searchDateType,
    isHideDNCPhoneNumbers,
    preForeclosureStatuses,
    distressedLevels,
    propertyTypes,
  ]);

  return {
    filters,
    listings,
    activeFilters,
    isHideDNCPhoneNumbers,
    sortDir,
    sortField,
    commType,
    toDate,
    fromDate,
    minPrice,
    maxPrice,
    numBeds,
    numBaths,
    sqFootage,
    postalCode,
    tags,
    isTagsAndSearch,
    setIsTagsAndSearch: (isAndSearch: boolean) => {
      updateSearchParams({ isTagsAndSearch: isAndSearch, currentPage: 1 });
    },
    mls,
    batchId,
    listingStatuses,
    searchDateType,
    searchParams,
    currentPage,
    pageSize,
    updateSearchParams,
    resetSearchFilters,
    scrollToTop,
    searchQuery,
    allListingsQuery,
    fetchListingsInChunks,
    csvApiUrl,
    suppressPriceFilters,
    includePreForeclosureStatusFilter,
    includeDistressedFilters,
    includeListingStatusFilter,
    includeSearchDateTypeFilter,
    includeMLSActivesFilter,
    includeBatchIdFilter,
    includeLookupNameFilter,
    includeLookupSourceFilter,
    includeMlsFilter,
    includePropertyTypeFilter,
    importedListingsImports,
    allSearchListingsQuery,
    totalRecords,
    productType: productType as EnumProductTypes,
    // Infinite scroll properties
    infiniteListings,
    infiniteListingsIsLoading: infiniteSearchQuery.isLoading,
    infiniteListingsFetchNextPage: infiniteSearchQuery.fetchNextPage,
    infiniteListingsHasNextPage: infiniteSearchQuery.hasNextPage,
    // Key that changes when search criteria change (excluding pagination)
    searchCriteriaKey,
  };
};

export default useSearchListings;
